import React, { memo, useEffect, useRef } from 'react'

import PropTypes from 'prop-types'
import { Droppable } from '@hello-pangea/dnd'
import { Box, IconButton, Tooltip, Typography } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import { FileText as FileTextIcon, Plus as PlusIcon } from 'react-feather'

import { Permitted } from 'components'
import { KanbanCard } from './components'

import helpers from 'helpers'

import constants from 'constants/index'
import useStyles from './styles'

const KanbanColumn = ({
  column,
  cards,
  totalCount,
  loading,
  hasMore,
  dragHandleProps,
  isDropDisabled,
  isDragging,
  onCreateCard,
  onEditCard,
  onDeleteCard,
  onLoadMore,
}) => {
  const { DRAG_TYPE_CARD } = constants.activities

  const classes = useStyles()
  const sentinelRef = useRef(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading && !isDragging) {
          onLoadMore(column.id)
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loading, isDragging, onLoadMore])

  const handleCreateCard = () => {
    helpers.mixpanel.track('Atividades', 'Task criada', {
      action: 'atividades_task_criada',
    })
    onCreateCard && onCreateCard(column.id)
  }

  const skeletonCount = isDragging
    ? 0
    : Math.min(
        Math.max(
          totalCount - cards.length,
          loading && totalCount === 0 ? 4 : 0,
        ),
        10,
      )

  return (
    <Box className={classes.column}>
      <Box className={classes.columnHeader} {...dragHandleProps}>
        <Box className={classes.columnHeaderLeft}>
          {column.title.length > 23 ? (
            <Tooltip title={column.title}>
              <Typography className={classes.columnTitle}>
                {column.title.slice(0, 23) + '…'}
              </Typography>
            </Tooltip>
          ) : (
            <Typography className={classes.columnTitle}>
              {column.title}
            </Typography>
          )}
          <Typography className={classes.columnCount}>
            ({totalCount})
          </Typography>
        </Box>
        <Box className={classes.columnActions}>
          <Permitted tag={constants.permissions.KANBAN_TASKS.MANAGE}>
            <IconButton
              size="small"
              className={classes.actionButton}
              onClick={handleCreateCard}
            >
              <PlusIcon size={16} />
            </IconButton>
          </Permitted>
        </Box>
      </Box>

      <Box className={classes.columnBody}>
        <Droppable
          droppableId={String(column.id)}
          type={DRAG_TYPE_CARD}
          isDropDisabled={isDropDisabled}
        >
          {(provided, snapshot) => (
            <Box className={classes.droppable}>
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={clsx(classes.droppableScroll, {
                  [classes.droppableDraggingOver]: snapshot.isDraggingOver,
                })}
              >
                {isEmpty(cards) && !snapshot.isDraggingOver && !loading && (
                  <Box className={classes.emptyState}>
                    <FileTextIcon size={28} className={classes.emptyIcon} />
                    <Typography className={classes.emptyTitle}>
                      Nenhuma atividade
                    </Typography>
                    <Typography className={classes.emptySubtitle}>
                      {column.title.length > 23
                        ? column.title.slice(0, 23) + '…'
                        : column.title}
                    </Typography>
                  </Box>
                )}

                {cards.map((card, index) => (
                  <KanbanCard
                    key={card.id}
                    card={card}
                    index={index}
                    columnRole={column.role}
                    onEdit={onEditCard}
                    onDelete={onDeleteCard}
                  />
                ))}

                {provided.placeholder}

                <div ref={sentinelRef} className={classes.sentinel} />

                {skeletonCount > 0 && (
                  <Box>
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="rect"
                        animation={loading ? 'wave' : false}
                        width="100%"
                        height={110}
                        className={classes.skeleton}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Droppable>

        <Permitted tag={constants.permissions.KANBAN_TASKS.MANAGE}>
          <Box
            className={classes.createButton}
            onClick={handleCreateCard}
            component="button"
          >
            <PlusIcon size={14} />
            Criar Atividade
          </Box>
        </Permitted>
      </Box>
    </Box>
  )
}

KanbanColumn.propTypes = {
  column: PropTypes.object.isRequired,
  cards: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  dragHandleProps: PropTypes.object,
  isDropDisabled: PropTypes.bool,
  isDragging: PropTypes.bool,
  onCreateCard: PropTypes.func,
  onEditCard: PropTypes.func,
  onDeleteCard: PropTypes.func,
  onLoadMore: PropTypes.func.isRequired,
}

export default memo(KanbanColumn)
